import React, { useState, useCallback, useEffect, useMemo } from 'react'
import { Stack, FormControl, Select, MenuItem, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import BoardList from '@components/Board/BoardList'
import BoardListCard from '@components/Board/BoardListCard'
import { useIsMobile } from '@/hooks/Responsive'
import { getVLabList } from '@/api/vx'
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@/data/nav'
import { visuallyHidden } from '@mui/utils'
import { cmmCd } from 'api/comp'
import { useAuth } from '@/components/AuthContext'

export function LectureToolbar({
  isMobile,
  category,
  subCategory,
  detailCategory,
  onCategoryChange,
  onSubCategoryChange,
  onDetailCategoryChange,
  majorOptions,
  midOptions,
  smallOptions,
}) {
  return (
    <Stack direction={isMobile ? 'column' : 'row'} spacing={1} sx={{ flex: 1, width: '100%' }}>
      {/* 대카테고리 */}
      <FormControl sx={{ minWidth: '150px' }}>
        <Typography component="label" sx={visuallyHidden} htmlFor="main-category">
          대카테고리 선택
        </Typography>
        <Select
          displayEmpty
          value={category}
          onChange={(e) => onCategoryChange(e.target.value)}
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) =>
                  isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
              },
            },
          }}
          inputProps={{ id: 'main-category' }}
          sx={{
            width: '100%',
            maxWidth: (theme) =>
              isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
          }}
        >
          <MenuItem value="">대카테고리</MenuItem>
          {majorOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 중카테고리 */}
      <FormControl sx={{ minWidth: '150px' }}>
        <Typography component="label" sx={visuallyHidden} htmlFor="sub-category">
          중카테고리 선택
        </Typography>
        <Select
          displayEmpty
          value={subCategory}
          onChange={(e) => onSubCategoryChange(e.target.value)}
          disabled={!category || midOptions.length === 0}
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) =>
                  isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
              },
            },
          }}
          inputProps={{ id: 'sub-category' }}
          sx={{
            width: '100%',
            maxWidth: (theme) =>
              isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
          }}
        >
          <MenuItem value="">중카테고리</MenuItem>
          {midOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* 소카테고리 */}
      <FormControl sx={{ minWidth: '150px' }}>
        <Typography component="label" sx={visuallyHidden} htmlFor="detail-category">
          소카테고리 선택
        </Typography>
        <Select
          displayEmpty
          value={detailCategory}
          onChange={(e) => onDetailCategoryChange(e.target.value)}
          disabled={!subCategory || smallOptions.length === 0}
          MenuProps={{
            PaperProps: {
              sx: {
                width: '100%',
                maxWidth: (theme) =>
                  isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
              },
            },
          }}
          inputProps={{ id: 'detail-category' }}
          sx={{
            width: '100%',
            maxWidth: (theme) =>
              isMobile ? `calc(100vw - ${theme.spacing(8)})` : '150px',
          }}
        >
          <MenuItem value="">소카테고리</MenuItem>
          {smallOptions.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  )
}

export default function VLabLecture() {
  const navigate = useNavigate()
  const lang = useLang()
  const { isMobile } = useIsMobile()
  const { user, loading, isAuthReady } = useAuth();

  const [confirmedSearch, setConfirmedSearch] = useState('')

  // 실시간 선택값
  const [category, setCategory] = useState('')
  const [subCategory, setSubCategory] = useState('')
  const [detailCategory, setDetailCategory] = useState('')

  // 검색 눌렀을 때 확정 값
  const [confirmedCategory, setConfirmedCategory] = useState('')
  const [confirmedSubCategory, setConfirmedSubCategory] = useState('')
  const [confirmedDetailCategory, setConfirmedDetailCategory] = useState('')

  // 옵션 리스트
  const [majorOptions, setMajorOptions] = useState([])
  const [midOptions, setMidOptions] = useState([])
  const [smallOptions, setSmallOptions] = useState([])


  const toOptions = (res) => {
    const list = res?.data ?? []
    return (Array.isArray(list) ? list : [])
      .map((it) => ({ value: it.comCd, label: it.comNm }))
      .filter((o) => o.value && o.label)
  }

  // 1) 최초: 대분류(PRSKDEX) 로드
  useEffect(() => {
    let alive = true

    const loadMajor = async () => {
      try {
        const res = await cmmCd('PRSKDEX')
        if (!alive) return
        setMajorOptions(toOptions(res))
      } catch (e) {
        if (!alive) return
        setMajorOptions([])
      }
    }

    loadMajor()

    return () => {
      alive = false
    }
  }, [])

  // 2) 대분류(category) 바뀌면: 중분류 로드 (grpCd = category)
  useEffect(() => {
    let alive = true

    const loadMid = async () => {
      // 대분류 선택 안 했으면 중/소 초기화
      if (!category) {
        setMidOptions([])
        setSmallOptions([])
        return
      }

      try {
        const res = await cmmCd(category)
        if (!alive) return
        setMidOptions(toOptions(res))
        setSmallOptions([])
      } catch (e) {
        if (!alive) return
        setMidOptions([])
        setSmallOptions([])
      }
    }

    loadMid()

    return () => {
      alive = false
    }
  }, [category])

  // 3) 중분류(subCategory) 바뀌면: 소분류 로드 (grpCd = subCategory)
  useEffect(() => {
    let alive = true

    const loadSmall = async () => {
      if (!subCategory) {
        setSmallOptions([])
        return
      }

      try {
        const res = await cmmCd(subCategory)
        if (!alive) return
        setSmallOptions(toOptions(res))
      } catch (e) {
        if (!alive) return
        setSmallOptions([])
      }
    }

    loadSmall()

    return () => {
      alive = false
    }
  }, [subCategory])

  const handleCategoryChange = (val) => {
    setCategory(val)
    setSubCategory('')
    setDetailCategory('')
  }

  const handleSubCategoryChange = (val) => {
    setSubCategory(val)
    setDetailCategory('')
  }

  const handleDetailCategoryChange = (val) => {
    setDetailCategory(val)
  }

  const handleDetailClick = (row) => {
    navigate(buildLangHref(`/contents/lectureview/${row.id}`, lang), { state: { row } })
  }

  const fetcher = useCallback(
    async ({ page = 1, pageSize = 6, recordSize = 6 }) => {
      const req = {
        page,
        pageSize,
        recordSize,
        searchNm: confirmedSearch || '',
        eduGrandDivCode: confirmedCategory || '',
        eduMidiumDivCode: confirmedSubCategory || '',
        eduSmallDivCode: confirmedDetailCategory || '',
        accountId: user?.accountId
      }

      const res = await getVLabList(req)
      const list = res?.data?.content ?? []

      const items = list.map((row, index) => ({
        id: row.eduLectureId || index,
        title: row.eduTitle ?? '',
        preview: false,
        interest: null,
        institution: row?.instYn === 'Y' ? true : false,
        // lectureLevel: index % 2 === 1 ? '초급' : '중급',
        lectureLevel: row?.ldfcyCdNm ?? '초급',
        sessionTotal: row.eduCourseCnt ?? '0',
        previewUrl: '',
        thumbUrl: (row?.filePath && row?.attachFileName) ? row.filePath + row.attachFileName: null,//row.thumbnailImg,
        Cat: row.eduGrandDivName || '',
        subCat: row.eduMidiumDivName || '',
        detailCat: row.eduSmallDivName || '',
      }))

      return {
        items,
        total: res?.data?.totalElements ?? list.length,
      }
    },
    [confirmedSearch, confirmedCategory, confirmedSubCategory, confirmedDetailCategory, user],
  )

  return (
    <BoardList
      view="card"
      columns={[]}
      fetcher={fetcher}
      initialPage={1}
      initialPageSize={6}
      initialSort={{ field: 'id', direction: 'desc' }}
      CardComponent={BoardListCard}
      storageKey="setpage_lecture"
      CardProps={{
        gridCols: 3,
        onDetailClick: handleDetailClick,
      }}
      toolbarLeft={
        <LectureToolbar
          isMobile={isMobile}
          category={category}
          subCategory={subCategory}
          detailCategory={detailCategory}
          onCategoryChange={handleCategoryChange}
          onSubCategoryChange={handleSubCategoryChange}
          onDetailCategoryChange={handleDetailCategoryChange}
          majorOptions={majorOptions}
          midOptions={midOptions}
          smallOptions={smallOptions}
        />
      }
      onSearchSubmit={(searchValue) => {
        setConfirmedSearch(searchValue)
        setConfirmedCategory(category)
        setConfirmedSubCategory(subCategory)
        setConfirmedDetailCategory(detailCategory)
      }}
      showDefaultToolbar={true}
    />
  )
}
